<template>
	<div class="page-dashboard" v-if="!!user">
		<div class="container-fluid">
			<!-- Table Menu -->
			<div class="table-menu flex-center">
				<div v-if="!smallScreen">
					<h2 class="text-primary mb-3">{{ $t(title) }}</h2>
					<div class="mb-4 pr-3">
						{{ $t('Create a list of your overlays, arrange and customize from existing predefined looks, to fit each streaming session') }}
					</div>
				</div>
				<div class="d-flex">
					<label
						v-if="!smallScreen"
						class="btn btn-default ml-a mr-2 mb-0 mw-fc">
						<div class="">
								<span>{{ $t('Import Rundown') }}</span>
								<input type="file" accept=".xlsx, .xls" class="hidden" @change="onImportSelected"/>
							</div>
					</label>
					<input
						type="text"
						class="form-control ml-a"
						:class="{'table-search': !smallScreen}"
						:placeholder="$t('Search')"
						@input="onKeywordChange"
						v-model="keyword"
					/>
				</div>
			</div>
			<div class="text-left" v-if="!isFetching && filteredAvailableItems.length === 0 && !keyword && !isCollaborator">
				<button
					class="btn btn-primary mr-3 mb-5"
					@click="createQuickRundown()">
					{{$t('GET QUICK START RUNDOWN SAMPLE')}}
				</button>
			</div>
			<div class="text-center mt-3" v-if="items.length === 0 && keyword && !isFetching">
				<h4>{{ $t('It seems that the Rundown you are looking for isn\'t available') }}</h4>
			</div>
			<!-- Recent Rundowns -->
			<div class="p-5 text-center" v-if="isFetching">
				<div class="is-loading large"></div>
			</div>
			<div
				class="mb-extreme"
				v-if="mode === 'all' || mode === 'recent'">
				<div class="flex-center mb-3">
					<router-link v-if="!isFetching && items.length !== 0 && !smallScreen" to="/rundowns/recent" class="text-white">
						{{ $t('Recent Rundown') }}
					</router-link>
					<router-link to="/rundowns/recent" class="text-white td-u ml-a" v-if="mode === 'all' && totalItems > 8 && !isFetching">
						{{ $t('Show All') }}
					</router-link>
					<router-link to="/rundowns" class="text-white td-u ml-a" v-else-if="mode!=='all'">
						{{ $t('Back') }}
					</router-link>
				</div>
				<div class="rundowns row" v-if="items.length !== 0">
					<div
						class="col-lg-3 col-md-4 mb-4"
						v-for="item in filteredItems"
						:key="item.id">
						<rundown-card
							v-if="items.length !== 0"
							:item="item"
							@duplicate="confirmDuplicateItem"
							@export="exportRundown(item,'excel')"
							@share="share"
							@archive="confirmArchive"
							@unarchive="confirmUnarchive"
							@remove="confirmRemoveItem"
							@rename="confirmRename"
						/>
					</div>
				</div>
				<!-- Table Pagination -->
				<pagination
					:current-page="page"
					:total-pages="lastPage"
					@select="onPageSelected(...arguments)"
					v-if="mode !== 'all'">
				</pagination>
			</div>

			<!-- Archived Rundowns -->
			<div
				class="mb-5"
				v-if="mode === 'all' || mode === 'archived'  && !isFetchingArchived">
				<div class="flex-center mb-3">
					<router-link v-if="archivedItems.length > 0" to="/rundowns/archived" class="text-white">
						<span>{{ $t('Archived Rundown') }}</span>
					</router-link>
					<router-link to="/rundowns/archived" class="text-white td-u ml-a" v-if="mode === 'all' && totalArchivedItems > 8">
						{{ $t('Show All') }}
					</router-link>
					<router-link to="/rundowns" class="text-white td-u ml-a" v-else-if="mode !== 'all'">
						{{ $t('Back') }}
					</router-link>
				</div>
				<div class="rundowns row" v-if="archivedItems.length > 0">
					<div
						class="col-lg-3 col-md-4 mb-4"
						v-for="(item, index) in archivedItems"
						:key="index">
						<rundown-card
							:item="item"
							@duplicate="confirmDuplicateItem"
							@export="confirmExportItem"
							@share="share"
							@archive="confirmArchive"
							@unarchive="confirmUnarchive"
							@remove="confirmRemoveItem"
							@rename="confirmRename"
						/>
					</div>
				</div>
				<div class="text-center hidden" v-else>
					{{ $t('No Archive Rundown available') }}
				</div>
				<!-- Table Pagination -->
				<pagination
					:current-page="archivedPage"
					:total-pages="lastArchivedPage"
					@select="onArchivedPageSelected(...arguments)"
					v-if="mode !== 'all'">
				</pagination>
			</div>
		</div>

		<!-- Modals -->
		<div class="modals">
			<!-- User Form -->
			<user-form
				:data="selected"
				@saved="insertItem"
				@close="isFormVisible = false"
				v-if="isFormVisible"
			/>

			<!-- Duplicate Confirmation -->
			<modal
				size="small"
				@close="isConfirmDuplicateVisible = false"
				v-if="isConfirmDuplicateVisible">
				<template slot="header">
					<div>{{ $t('Confirm Duplicate') }}</div>
				</template>
				<template slot="body">
					{{ $t('Duplicate rundown') }} {{ showModalName(selected ? selected.name : '-') }}?
				</template>
				<template slot="footer">
					<div class="w-100 flex-center m-0">
						<button
							class="btn btn-primary"
							:class="{ 'is-loading': isDuplicating }"
							@click="duplicateItem(selected)">
							{{ $t('Yes, Proceed') }}
						</button>
						<button
							class="btn btn-default"
							@click="isConfirmDuplicateVisible = false">
							{{ $t('Cancel') }}
						</button>
					</div>
				</template>
			</modal>

			<!-- Archive -->
			<modal
				size="small"
				@close="isConfirmArchiveVisible = false"
				v-if="isConfirmArchiveVisible">
				<template slot="header">
					<div>{{ $t('Confirm Archive') }}</div>
				</template>
				<template slot="body">
					{{ $t('Archive rundown') }} {{ showModalName(selected ? selected.name : '-') }}?
				</template>
				<template slot="footer">
					<div class="w-100 flex-center m-0">
						<button
							class="btn btn-primary"
							:class="{ 'is-loading': isArchiving }"
							@click="archive(selected, true)">
							{{ $t('Yes, Proceed') }}
						</button>
						<button
							class="btn btn-default"
							@click="isConfirmArchiveVisible = false">
							{{ $t('Cancel') }}
						</button>
					</div>
				</template>
			</modal>

			<!-- Unarchive -->
			<modal
				size="small"
				@close="isConfirmUnarchiveVisible = false"
				v-if="isConfirmUnarchiveVisible">
				<template slot="header">
					<div>{{ $t('Confirm Unarchive') }}</div>
				</template>
				<template slot="body">
					{{ $t('Unarchive rundown') }} {{ showModalName(selected ? selected.name : '-') }}?
				</template>
				<template slot="footer">
					<div class="w-100 flex-center m-0">
						<button
							class="btn btn-primary"
							:class="{ 'is-loading': isArchiving }"
							@click="archive(selected, false)">
							{{ $t('Yes, Proceed') }}
						</button>
						<button
							class="btn btn-default"
							@click="isConfirmUnarchiveVisible = false">
							{{ $t('Cancel') }}
						</button>
					</div>
				</template>
			</modal>

			<!-- Delete Confirmation -->
			<modal
				size="small"
				@close="isConfirmRemoveVisible = false"
				v-if="isConfirmRemoveVisible">
				<template slot="header">
					{{ $t('Confirm Delete') }}
				</template>
				<template slot="body">
					<div class="text-white" v-if="selectedTotalAsset > 0">{{ $t(`There are ${selectedTotalAsset} assets in this rundown`) }}</div>
					<div>{{ $t('Are you sure want to delete') }} {{ showModalName(selected ? selected.name : '-') }}?</div>
				</template>
				<template slot="footer">
					<div class="w-100 flex-center" :class="{'mt-4': selectedTotalAsset > 0}">
						<button class="btn btn-primary" :class="{ 'is-loading': isRemoving }" @click="removeItem(selected)">
							{{ $t('Yes, Delete It') }}
						</button>
						<button class="btn btn-default" @click="isConfirmRemoveVisible = false">
							{{ $t('Cancel') }}
						</button>
					</div>
				</template>
			</modal>

			<!-- Share -->
			<modal
				size="small"
				@close="isShareVisible = false"
				v-if="isShareVisible">
				<template slot="header">
					{{ $t('Share Rundown') }}
				</template>
				<template slot="body">
					<social-share :url="shareUrl" />
				</template>
				<template slot="footer">
					<button
						class="btn btn-primary"
						@click="isShareVisible = false">
						{{ $t('OK') }}
					</button>
				</template>
			</modal>
			<!-- Rename Rundown -->
			<modal
				size="small"
				@close="isRenameVisible = false"
				v-if="isRenameVisible"
				>
				<template slot="header">
					{{ $t('Rename Rundown') }}
				</template>
				<template slot="body">
					<div class="form-group mb-3">
						<input
							type="text"
							class="form-control"
							:placeholder="$t('Rundown Name')"
							v-model="rundownName"
						/>
					</div>
				</template>
				<template slot="footer">
					<button
						type="button"
						class="btn btn-primary"
						:class="{ 'is-loading': isSavingRundown }"
						@click="updateRundown(selected)">
						{{ $t('Save') }}
					</button>
					<button
						type="button"
						class="btn btn-default"
						@click="isRenameVisible = false">
						{{ $t('Cancel') }}
					</button>
				</template>
			</modal>
		</div>
		<modal-first-greeting v-if="userFirstLogin" :rundowns="items" @close="userSetLogin"></modal-first-greeting>
		<div v-for="item in userAssignePackage" :key="item.id">
			<modal-quotation-confirm v-if="item.is_active" @close="redirectToSubscribe" @declined="declineQuotation" :dataAssignePackage="item" ></modal-quotation-confirm>
		</div>
		<modal-rundown-take-over></modal-rundown-take-over>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import XLSX from 'xlsx';
import Modal from '@/components/Modal.vue';
import ModalRundownTakeOver from '@/components/ModalRundownTakeOver.vue';
import ModalFirstGreeting from '@/components/ModalFirstGreeting.vue';
import ModalQuotationConfirm from '@/components/ModalQuotationConfirm.vue';
import UserForm from '@/components/UserForm.vue';
import RundownCard from '@/components/RundownCard.vue';
import Pagination from '@/components/Pagination.vue';
import SocialShare from '@/components/SocialShare.vue';
import {
	getAxiosErrorMessage, delay, share, duplicateVar, downloadBlob, elpisisString,
} from '@/lib/helper';
import rundownsApi from '@/api/rundowns';
import rundownItemsApi from '@/api/rundownItems';

import usersApi from '@/api/user';

export default {
	name: 'Rundowns',
	props: {
		mode: {
			type: String,
			default: 'all',
		},
	},
	components: {
		Modal,
		UserForm,
		RundownCard,
		ModalRundownTakeOver,
		ModalFirstGreeting,
		ModalQuotationConfirm,
		Pagination,
		SocialShare,
	},
	data() {
		return {
			page: 1,
			limit: 8,
			orderBy: 'created_at',
			sortBy: 'desc',
			keyword: '',
			role: 'client',
			isAll: false,
			items: [],
			lastPage: 1,
			selected: null,
			totalItems: 0,
			archivedPage: 1,
			archivedLimit: 8,
			archivedOrderBy: 'created_at',
			archivedSortBy: 'desc',
			totalArchivedItems: 0,
			lastArchivedPage: 1,
			archivedIsAll: false,
			isFetchingArchived: false,
			roles: [
				{ key: 'all', label: 'All Roles' },
				{ key: 'client', label: 'Client' },
				{ key: 'super_admin', label: 'Super Admin' },
				{ key: 'collaborator', label: 'Collaborator' },
			],
			filterRole: null,
			isFormVisible: false,
			isConfirmRemoveVisible: false,
			isRemoving: false,
			title: 'Rundown',
			isConfirmDuplicateVisible: false,
			isConfirmArchiveVisible: false,
			isConfirmUnarchiveVisible: false,
			isExporting: false,
			archivedItems: [],
			isShareVisible: false,
			isExportVisible: false,
			isArchiving: false,
			isDuplicating: false,
			userFirstLogin: false,
			isFetching: false,
			isImporting: false,
			isRenameVisible: false,
			rundownName: '',
			isSavingRundown: false,
			userAssignePackage: [],
			quickStarting: false,
			smallScreen: false,
		};
	},
	sockets: {
		connect() {
		},
		rundown_update(payload) {
			this.refetchUpdate();
			this.nameRundown = payload.name;
		},
	},
	methods: {
		onPageSelected(page) {
			this.fetch(true, page);
		},
		onArchivedPageSelected(page) {
			this.fetchArchived(true, page);
		},
		fetchData() {
			this.fetch(true, false);
			this.fetchArchived(true);
		},
		refetchUpdate() {
			const params = duplicateVar(this.fetchParams);
			params.is_archived = false;
			const callback = (response) => {
				const items = response.data;
				if (items.length < this.limit) this.isAll = true;
				this.totalItems = response.total;
				this.lastPage = response.lastPage;
				this.insertItems(items);
				this.isFetching = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: this.$t(this.title),
					text: message,
				});
				this.isFetching = false;
			};
			rundownsApi.getList(params, callback, errorCallback);
		},
		fetch(reset = false, page) {
			this.isFetching = true;
			if (reset) {
				this.page = 1;
				this.items = [];
			}
			if (page) this.page = page;
			const params = duplicateVar(this.fetchParams);
			params.is_archived = false;
			const callback = (response) => {
				const items = response.data;
				if (items.length < this.limit) this.isAll = true;
				this.totalItems = response.total;
				this.lastPage = response.lastPage;
				this.insertItems(items);
				this.isFetching = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: this.$t(this.title),
					text: message,
				});
				this.isFetching = false;
			};
			rundownsApi.getList(params, callback, errorCallback);
		},
		fetchArchived(reset = false, page) {
			this.isFetchingArchived = true;
			if (reset) {
				this.archivedPage = 1;
				this.archivedItems = [];
			}
			if (page) this.archivedPage = page;
			const params = {
				page: this.archivedPage,
				limit: this.archivedLimit,
				order_by: this.archivedOrderBy,
				sort_by: this.archivedSortBy,
				user_id: this.user.id,
				is_archived: true,
			};
			const callback = (response) => {
				const items = response.data;
				if (items.length < this.archivedLimit) this.archivedIsAll = true;
				this.totalArchivedItems = response.total;
				this.lastArchivedPage = response.lastPage;
				this.insertItems(items);
				this.isFetchingArchived = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: this.$t(this.title),
					text: message,
				});
				this.isFetchingArchived = false;
			};
			rundownsApi.getList(params, callback, errorCallback);
		},
		onKeywordChange() {
			delay(() => {
				// this.fetch(true);
				this.fetchData(true);
			}, 500);
		},
		insertItem(item) {
			const index = this.items.findIndex(({ id }) => id === item.id);
			if (index === -1) {
				this.items.push(item);
			} else {
				this.$set(this.items, index, item);
			}
		},
		addItem() {
			this.selected = null;
			this.isFormVisible = true;
		},
		editItem(item) {
			this.selected = item;
			this.isFormVisible = true;
		},
		confirmRemoveItem(item) {
			this.selected = item;
			this.isConfirmRemoveVisible = true;
		},
		confirmDuplicateItem(item) {
			this.selected = item;
			this.isConfirmDuplicateVisible = true;
		},
		confirmArchive(item) {
			this.selected = item;
			this.isConfirmArchiveVisible = true;
		},
		confirmUnarchive(item) {
			this.selected = item;
			this.isConfirmUnarchiveVisible = true;
		},
		confirmExportItem(item) {
			this.selected = item;
			this.isExportVisible = true;
		},
		confirmRename(item) {
			this.rundownName = item.name;
			this.selected = item;
			this.isRenameVisible = true;
		},
		removeItem(item) {
			this.isRemoving = true;
			const callback = (response) => {
				const message = response.message;
				if (item.is_archived) {
					const index = this.archivedItems.findIndex(({ id }) => id === item.id);
					if (index !== -1) {
						this.archivedItems.splice(index, 1);
					}
				} else {
					const index = this.items.findIndex(({ id }) => id === item.id);
					if (index !== -1) {
						this.items.splice(index, 1);
					}
				}
				this.isRemoving = false;
				this.isConfirmRemoveVisible = false;
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Remove Rundown',
					text: message,
				});
			};
			const errorCallback = (e) => {
				this.isRemoving = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Remove Rundown',
					text: message,
				});
			};
			rundownsApi.delete(item.id, callback, errorCallback);
		},
		archive(item, value = true) {
			this.isArchiving = true;
			const params = {
				is_archived: value,
			};
			const callback = (response) => {
				const message = response.message;
				const data = response.data;
				if (value) {
					const index = this.items.findIndex(({ id }) => id === item.id);
					if (index !== -1) {
						this.items.splice(index, 1);
						this.archivedItems.push(data);
					}
				} else {
					const index = this.archivedItems.findIndex(({ id }) => id === item.id);
					if (index !== -1) {
						this.archivedItems.splice(index, 1);
						this.items.push(data);
					}
				}
				this.isArchiving = false;
				this.isConfirmArchiveVisible = false;
				this.isConfirmUnarchiveVisible = false;
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Archive Rundown',
					text: message,
				});
				// this.fetchData();
			};
			const errorCallback = (e) => {
				this.isArchiving = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Archive Rundown',
					text: message,
				});
			};
			rundownsApi.update(item.id, params, callback, errorCallback);
		},
		duplicateItem(item) {
			this.isDuplicating = true;
			// const name = `Duplicate of ${item.name}`;
			const callback = (response) => {
				const duplicatedItem = response.data;
				const message = response.message;
				// this.insertItems([duplicatedItem]);
				this.items.push(duplicatedItem);
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Duplicate Rundown',
					text: message,
				});
				this.isDuplicating = false;
				this.isConfirmDuplicateVisible = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Duplicate Rundown',
					text: message,
				});
				this.isDuplicating = false;
			};
			rundownsApi.duplicate(item.id, callback, errorCallback);
		},
		share(item) {
			this.selected = item;
			const origin = window.location.origin;
			const url = `${origin}/rundowns/editor/${atob(item.slug)}`;
			if (navigator.share) {
				const payload = {
					title: 'Grafias',
					text: `Rundown ${item.name}`,
					url,
				};
				share(payload);
			} else {
				this.isShareVisible = true;
			}
		},
		exportRundown(item, type) { // get from backend because we dont have the data on the front end side
			this.isExporting = true;
			const params = {
				page: this.page,
				limit: this.limit,
				order_by: this.orderBy,
				sort_by: this.sortBy,
				ids: JSON.stringify([item.id]),
			};
			const callback = (response) => {
				const contents = response;
				downloadBlob(contents, { name: item.name, type });
				this.isExporting = false;
			};
			const errorCallback = () => {
				this.isExporting = false;
			};
			rundownsApi.exportList(type, params, callback, errorCallback);
		},
		createQuickRundown() {
			this.quickStarting = false;
			const callback = () => {
				this.fetch();
				this.quickStarting = false;
			};
			const errorCallback = () => {
				this.quickStarting = false;
			};
			rundownsApi.quickStartRundown(callback, errorCallback);
		},
		onImportSelected(e) {
			// console.log(e);
			const files = e.target.files;
			if (files.length > 0) {
				if (files[0].name.includes('.xlsx') || files[0].name.includes('.xls')) {
					const file = files[0];
					const reader = new FileReader();
					reader.onload = (e) => {
						const data = e.target.result;
						try {
							const workbook = XLSX.read(data, {
								type: 'binary',
							});
							workbook.SheetNames.forEach((sheetName) => {
								// Here is your object
								const XlRowObject = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName]);
								this.create(sheetName, XlRowObject);
							});
						} catch (error) {
							this.$notify({
								group: 'app',
								type: 'warning',
								title: 'Upload Media',
								text: 'File Corrupt, cannot read data',
							});
						}
					};
					// eslint-disable-next-line no-unused-vars
					reader.onerror = (ex) => {
					};
					reader.readAsBinaryString(file);
				} else {
					this.$notify({
						group: 'app',
						type: 'warning',
						title: 'Upload Media',
						text: 'Unsupported file',
					});
					// eslint-disable-next-line
					return;
				}
			}
		},
		createBulkRundownItem(id, items) {
			this.isImporting = true;
			const rundownId = id;
			const callback = () => {
				this.fetch();
				// const importedItems = response.data;
				// this.insertItems(importedItems);
				this.isImporting = false;
				const message = this.$t('Import Success');
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Rundown Assets',
					text: message,
				});
			};
			const errorCallback = () => {
				this.isImporting = false;
			};
			rundownItemsApi.createBulk(rundownId, items, callback, errorCallback);
		},
		create(name, items) {
			this.isImporting = true;
			const callback = (response) => {
				const item = response.data;
				this.insertItems([item]);
				this.createBulkRundownItem(item.id, items);
				const message = response.message;
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Import Rundowns',
					text: message,
				});
				// this.isImporting = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Import Rundowns',
					text: message,
				});
				this.isImporting = false;
			};
			const params = {
				name,
			};
			rundownsApi.create(params, callback, errorCallback);
		},
		updateRundown(item) {
			this.isSavingRundown = true;
			const callback = (response) => {
				const itemRundown = response.data;
				// eslint-disable-next-line
				item.name = itemRundown.name;
				if (item.is_archived) {
					const index = this.archivedItems.findIndex(({ id }) => id === item.id);
					this.archivedItems[index].slug = itemRundown.slug;
				} else {
					const index = this.items.findIndex(({ id }) => id === item.id);
					this.items[index].slug = itemRundown.slug;
				}
				this.$notify({
					group: 'app',
					type: 'success',
					title: 'Rundown name has been changed',
					text: 'Rundown name has been changed',
				});
				this.isSavingRundown = false;
				this.isRenameVisible = false;
			};
			const errorCallback = () => {
				this.isSavingRundown = false;
				this.isRenameVisible = false;
			};
			const params = {
				name: this.rundownName,
			};
			rundownsApi.update(item.id, params, callback, errorCallback);
		},
		insertItems(items) {
			for (let index = 0; index < items.length; index++) {
				const item = items[index];
				if (item.is_archived) {
					const index = items.findIndex(({ id }) => id === item.id);
					if (index === -1) {
						this.archivedItems.push(item);
					} else {
						this.$set(this.archivedItems, index, item);
					}
				} else {
					const index = items.findIndex(({ id }) => id === item.id);
					if (index === -1) {
						this.items.push(item);
					} else {
						this.$set(this.items, index, item);
					}
				}
				// }
			}
		},
		userSetLogin() {
			const callback = (response) => {
				const items = response.data;
				this.userFirstLogin = items.is_first_time_login;
				this.userAssignePackage = items.assignPackages;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Users',
					text: message,
				});
			};
			usersApi.get(this.user.id, callback, errorCallback);
		},
		showModalName(str) {
			return elpisisString(str);
		},
		redirectToSubscribe(packageId) {
			const callback = (response) => {
				this.userAssignePackage = response.data.assignPackages;
				this.$router.push(`/settings?menu=payment&packageId=${packageId}`);
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Users',
					text: message,
				});
			};
			usersApi.get(this.user.id, callback, errorCallback);
		},
		declineQuotation() {
			const callback = (response) => {
				this.userAssignePackage = response.data.assignPackages;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Users',
					text: message,
				});
			};
			usersApi.get(this.user.id, callback, errorCallback);
		},
		onResizeWidth() {
			if (window.innerWidth <= 1024) {
				this.smallScreen = true;
			} else {
				this.smallScreen = false;
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			isCollaborator: 'isCollaborator',
		}),
		fetchParams() {
			const params = {
				page: this.page,
				limit: this.limit,
				order_by: this.orderBy,
				sort_by: this.sortBy,
				user_id: this.user.id,
				// role: this.role,
			};
			const keyword = this.keyword.trim();
			if (keyword !== '') params.keyword = keyword;
			return params;
		},
		filteredItems() {
			const filtered = this.items.filter(item => !item.is_archived);
			return filtered;
		},
		filteredAvailableItems() {
			const filtered = this.items.filter(item => !item.is_disabled);
			return filtered;
		},
		shareUrl() {
			return this.selected ? `${window.location.origin}/rundowns/${atob(this.selected.slug)}` : '';
		},
		activeUsers() {
			const activeUsers = this.items && this.items.activeUsers !== '' ? JSON.parse(this.items.activeUsers) : null;
			return activeUsers;
		},
		selectedTotalAsset() {
			const total = this.selected && this.selected.__meta__ ? this.selected.__meta__.rundownItems_count : 0;
			return total;
		},
	},
	created() {
		window.addEventListener('resize', this.onResizeWidth);
		this.onResizeWidth();
		if (this.user) {
			const userId = this.user && this.user.id ? this.user.id : '';
			const roomId = `user_${userId}`;
			this.$socket.client.emit('join', roomId);
			this.fetchData(true);
			this.userSetLogin();
		}
	},
	mounted() {
		window.addEventListener('resize', this.onResizeWidth);
		this.onResizeWidth();
	},
	watch: {
		user(newUser, oldUser) {
			if (!oldUser || (newUser.settings === oldUser.settings)) {
				this.fetchData();
			}
			this.userSetLogin();
		},
		mode() {
			this.fetchData();
		},
		rundownName(val) {
			this.rundownName = val.replace(/[^A-Z0-9 ]/gi, '');
		},
	},
};
</script>
